export enum PlanComparisonFeatures {
  professionalMonitoring = 'Professional Monitoring',
  activeGuard = 'SimpliSafe® Active Guard Outdoor Protection',
  videoVerification = 'Video Verification',
  intruderIntervention = 'Intruder Intervention',
  cameraRecording = 'Unlimited Camera Recording',
  appControl = 'App Control',
  cellularConnection = 'Built-in Cellular Connection',
  noContract = 'No Contracts',
  multiCamera = 'Multi-camera live stream',
  accessPins = 'Unique Access PINs',
  antiTheft = 'Anti-Theft Guarantee',
  customerSupport = 'Elevated Customer Support'
}

type Feature = {
  readonly feature: PlanComparisonFeatures
  readonly description: React.ReactNode
}

type PlanComparisonTableData = {
  readonly features: readonly Feature[]
}

export const rowHeaders: PlanComparisonTableData = {
  features: [
    {
      feature: PlanComparisonFeatures.professionalMonitoring,
      description: (
        <>
          <strong>Professional monitoring:</strong> Our security agents are
          ready around the clock to request dispatch in the event of a break-in,
          fire, or medical emergency.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.activeGuard,
      description: (
        <>
          <strong>SimpliSafe® Active Guard Outdoor Protection: </strong>A double
          layer of outdoor defense — advanced AI detects threats and live agents
          intervene to proactively prevent crime outside your home while your
          system is armed.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.videoVerification,
      description: (
        <>
          <strong>Video Verification: </strong>If your cameras capture video
          evidence during an alarm, agents will relay information to emergency
          dispatch, resulting in faster police response compared to unverified
          alarms.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.intruderIntervention,
      description: (
        <>
          <strong>Intruder intervention: </strong>Agents can use the Smart Alarm
          Indoor Camera to see and speak to intruders to help stop crime in real
          time during an alarm.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.cameraRecording,
      description: (
        <>
          <strong>Unlimited camera recordings</strong> &amp; 30 day cloud
          storage.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.appControl,
      description: (
        <>
          <strong>Control your system in the app: </strong>Arm, disarm, confirm
          or cancel alarms using the app.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.cellularConnection,
      description: (
        <>
          <strong>Built-in cellular connection and back-up battery: </strong>
          The Base Station includes a cellular connection that keeps you linked
          to professional monitoring and a back-up battery that lasts up to 24
          hours in case there’s a power outage.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.noContract,
      description: (
        <>
          <strong>No contracts. No cancellation fees:</strong> You’ll never be
          locked into a long term contract, so you can cancel any time.
        </>
      )
    }
  ]
}

export const businessRowHeaders: PlanComparisonTableData = {
  features: [
    {
      feature: PlanComparisonFeatures.professionalMonitoring,
      description: (
        <>
          <strong>Professional monitoring:</strong> Our security agents are
          ready around the clock to request dispatch in the event of a break-in,
          fire, or medical emergency.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.multiCamera,
      description: (
        <>
          <strong>Multi-camera live stream:</strong> View live video from
          multiple SimpliSafe cameras at the same time — with full-screen
          viewing for each camera — making it easier to detect suspicious
          activity across different areas all in one place.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.activeGuard,
      description: (
        <>
          <strong>SimpliSafe® Active Guard Outdoor Protection: </strong>A double
          layer of outdoor defense — advanced AI detects threats and live agents
          intervene to proactively prevent crime outside your business while
          your system is armed.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.intruderIntervention,
      description: (
        <>
          <strong>Intruder intervention: </strong>Agents can use the Smart Alarm
          Indoor Camera to see and speak to intruders to help stop crime in real
          time during an alarm.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.videoVerification,
      description: (
        <>
          <strong>Video Verification: </strong>If your cameras capture video
          evidence during an alarm, agents will relay information to emergency
          dispatch, resulting in faster police response compared to unverified
          alarms.
        </>
      )
    },

    {
      feature: PlanComparisonFeatures.cameraRecording,
      description: (
        <>
          <strong>Unlimited camera recordings</strong> &amp; 30 day cloud
          storage for up to 10 cameras.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.accessPins,
      description: (
        <>
          <strong>Unique access PINs:</strong> Up to 10 unique PINs let you and
          your employees arm and disarm the system, and confirm or cancel alarms
          using the app.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.appControl,
      description: (
        <>
          <strong>Greater app control access: </strong>Expanded user roles give
          owners more control over employee access.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.cellularConnection,
      description: (
        <>
          <strong>Built-in cellular connection and back-up battery: </strong>
          The Base Station includes a cellular connection that keeps you linked
          to professional monitoring and a back-up battery that lasts up to 24
          hours in case there’s a power outage.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.antiTheft,
      description: (
        <>
          <strong>Anti-Theft Guarantee:</strong> Get up to $500 toward your
          insurance deductible if you experienced loss from a break-in when your
          system was armed. See{' '}
          <a href="/legal/anti-theft" target="_blank">
            Terms & Conditions
          </a>{' '}
          for full details.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.noContract,
      description: (
        <>
          <strong>No contracts. No cancellation fees:</strong> You’ll never be
          locked into a long term contract, so you can cancel any time.
        </>
      )
    },
    {
      feature: PlanComparisonFeatures.customerSupport,
      description: (
        <>
          <strong>Elevated customer support:</strong> Access to support tailored
          to the needs of small businesses, available daily from 8:00 am to 9:30
          pm ET. Get help with everything from billing to device
          troubleshooting.
        </>
      )
    }
  ]
}
